import { IKycStatus } from 'types/authKyc.types';
import { AuthApi, AuthUserApi } from '@lib/api';
import { TVerifyEmailResponsePayload } from 'types/auth';
import { IRegisterPayload, IRegisterRes, IResolvedAuthTokens } from 'types/auth-requests.types';

/**
 * Log user with credentials
 * @param email email of user
 * @param password pass of user
 * @returns promise with login token
 */
export function login(email: string, password: string) {
  return AuthApi.request<IRegisterRes>('/v1/login', {
    method: 'POST',
    data: {
      email,
      password,
    },
  });
}

/**
 * Register user in hilbi
 * @param data data to register
 * @returns promise
 */
export function register(data: IRegisterPayload) {
  return AuthApi.request<IRegisterRes>('/v1/registration', {
    method: 'POST',
    data,
  });
}

/**
 * Get real users tokens from provisioning token
 * @param loginToken login token from auth
 * @returns promise with tokens
 */
export function getAuthTokens(loginToken: string) {
  return AuthApi.request<IResolvedAuthTokens>('/v1/login/get-token', {
    headers: {
      Authorization: `Bearer ${loginToken}`,
    },
  });
}

/**
 * Verify user email
 * @param emailCode email code form user
 * @param token token from verification request
 * @returns promise with tokens
 */
export function verifyEmail(emailCode: string, token: string) {
  return AuthApi.request<TVerifyEmailResponsePayload | { recoveryToken: string }>(
    '/v1/verify/email',
    {
      method: 'POST',
      data: {
        code: emailCode,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

/**
 * Resend email with verification code
 * @param token token from non verified user
 * @returns promise
 */
export function resendVerificationEmail(token: string) {
  return AuthApi.request('/v1/resend/email', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Request reset
 * @param email email of user
 * @returns promise
 */
export function resetPassword(email: string) {
  return AuthApi.request<{ recoveryToken: string }>('/v1/user/recovery-password-request', {
    method: 'POST',
    data: {
      email,
    },
  });
}

/**
 * Set new password with auth token
 * @param recoveryToken token from request
 * @param password new password
 * @returns promise
 */
export function recoveryPassword(recoveryToken: string, password: string) {
  return AuthApi.request('/v1/user/recovery-password', {
    method: 'POST',
    data: {
      password,
      password_confirmation: password,
    },
    headers: {
      Authorization: `Bearer ${recoveryToken}`,
    },
  });
}

export function getKycStatus() {
  return AuthUserApi.request<IKycStatus>('/v1/area/kyc', {
    method: 'GET',
  });
}

/**
 * Get API tokens for SumSub SDK
 * @returns promise
 */
export function getKycTokens() {
  return AuthUserApi.request<{
    token: string;
    userId: string;
  }>('/v1/area/user/get-sumsub-token', {
    method: 'GET',
  });
}

/**
 * Get countries enabled by merchant
 * @returns countries
 */
export function getAuthCountries() {
  return AuthUserApi.request<Array<string>>('/v1/merchant/get-countries', {
    method: 'GET',
  });
}

/**
 * Verify user phone
 * @param phoneCode phone code form user
 * @param token token from verification request
 * @returns promise with tokens
 */
export function verifyPhone(phoneCode: string, token: string) {
  return AuthApi.request<TVerifyEmailResponsePayload | { loginToken: string }>('/v1/verify/sms', {
    method: 'POST',
    data: {
      code: phoneCode,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Resend phone with verification code
 * @param token token from non verified user
 * @returns promise
 */
export function resendVerificationPhone(token: string) {
  return AuthApi.request('/v1/resend/sms', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Change user password
 * @param oldPassword current pass
 * @param password new pass
 * @returns promise
 */
export function changePassword(oldPassword: string, password: string) {
  return AuthUserApi.request('/v1/area/user/change-password', {
    method: 'PUT',
    data: {
      oldPassword,
      password,
      passwordConfirmation: password,
    },
  });
}

export function uploadAvatar(data: FormData) {
  return AuthUserApi.request<{ data: { avatar: string } }>('/v1/area/user/avatar', {
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
  });
}
